import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SOURCE_PROVIDER_OPTIONS, sourceProviderLabel } from '../../../../core/utils/provider-label.util';
import { resourceTypeLabel } from '../../../../core/utils/resource-type-label.util';
import { Router, ActivatedRoute } from '@angular/router';
import {
  PoDialogService,
  PoDisclaimer,
  PoDisclaimerGroup,
  PoModalAction,
  PoModalComponent,
  PoPageAction,
  PoPageFilter,
  PoPageListComponent,
  PoSelectOption,
  PoTableAction,
  PoTableColumn,
  PoTableComponent
} from '@po-ui/ng-components';

import { MainService } from 'src/app/app.service';
import { NotificationServiceHM } from 'src/app/menu/notification-item/notification-item.service';
import {
  ALL_CATEGORY_OPTIONS,
  FINDING_KIND_OPTIONS,
  KIND_OPTIONS,
  LANGUAGE_OPTIONS,
  LEVER_OPTIONS,
  RESOLUTION_OPTIONS,
  RESOURCE_TYPE_OPTIONS,
  categoryLabel
} from '../../curation-constants';
import {
  CurateResponse,
  CurationCuratedEvent,
  CurationExportFilters,
  CurationKind,
  CurationQueueFilters,
  CurationQueueItem,
  CurationQueueResponse,
  CurationQueueSortBy,
  CurationSortOrder,
  CurationStatus
} from '../../interfaces/curation.interface';
import { CurationService } from '../../curation.service';
import { CurationCountsService } from '../../curation-counts.service';
import { CurationModalComponent } from '../curation-modal/curation-modal.component';
import { buildCurationSnapshotView } from '../../utils/curation-snapshot.util';
import { formatCurationDateTime, formatCurationReviewLabel, getCurationFeedbackUsers } from '../../utils/curation-format.util';
import { Period, defaultPeriod, rehydratePeriod } from 'src/app/menu/smart-review/core/utils/period.util';
import {
  UI_PREF_KEYS, clearPref, hasPref, readPref, sanitizeString, sanitizeStringArray, writePref
} from 'src/app/menu/smart-review/core/utils/ui-prefs.util';
import {
  applyColumnsPref, sanitizeColumnsPref, toColumnsPref
} from 'src/app/menu/smart-review/core/utils/table-columns-pref.util';
import { getApiErrorMessage } from 'src/app/menu/smart-review/core/utils/http-error-message.util';
import { FINDING_RESOLUTIONS } from 'src/app/menu/smart-review/review/review-feedback.constants';
import { FeedbackResolution } from 'src/app/menu/smart-review/review/interfaces/review-feedback.interface';
import { formatSeverityLabel } from 'src/app/menu/smart-review/review/utils/severity.util';

/** Fila de Curadoria — grids por status com filtros avançados + curar/descartar. */
@Component({
  selector: 'app-curation-queue',
  templateUrl: './curation-queue.component.html',
  styleUrls: ['./curation-queue.component.css']
})
export class CurationQueueComponent implements OnInit, OnDestroy {
  @ViewChild(CurationModalComponent, { static: true }) curationModal!: CurationModalComponent;
  @ViewChild('poPageList', { static: true }) poPageList!: PoPageListComponent;
  @ViewChild('advancedFilterModal', { static: true }) advancedFilterModal!: PoModalComponent;

  /**
   * A instância do po-table. `columns` dela é o array VIVO — o único lugar onde o
   * gerenciador continua escrevendo o alfinete depois do primeiro liga/desliga. Ver
   * `toColumnsPref`.
   */
  @ViewChild(PoTableComponent) poTable?: PoTableComponent;

  readonly resolutionOptions: PoSelectOption[] = RESOLUTION_OPTIONS;
  readonly kindOptions: PoSelectOption[] = KIND_OPTIONS;
  readonly findingKindOptions: PoSelectOption[] = FINDING_KIND_OPTIONS;
  readonly resourceTypeOptions: PoSelectOption[] = RESOURCE_TYPE_OPTIONS;
  readonly sourceProviderOptions: PoSelectOption[] = SOURCE_PROVIDER_OPTIONS;
  readonly languageOptions: PoSelectOption[] = LANGUAGE_OPTIONS;
  readonly categoryOptions: PoSelectOption[] = ALL_CATEGORY_OPTIONS;
  readonly leverOptions: PoSelectOption[] = LEVER_OPTIONS;

  /** Tooltips dos votos — mesmo texto do card de feedback da tela de review. */
  readonly voteTooltips = {
    truePositive:  this.tooltipFor('true_positive'),
    falsePositive: this.tooltipFor('false_positive'),
    wontFix:       this.tooltipFor('wont_fix')
  };

  private tooltipFor(value: FeedbackResolution): string {
    const r = FINDING_RESOLUTIONS.find(o => o.value === value)!;
    return `${r.label} — ${r.hint}`;
  }

  readonly statusTabs: { value: CurationStatus; label: string }[] = [
    { value: 'pending', label: 'Pendentes' },
    { value: 'curated', label: 'Curados' },
    { value: 'discarded', label: 'Descartados' }
  ];
  activeStatus: CurationStatus = 'pending';
  counts: Record<CurationStatus, number> = { pending: 0, curated: 0, discarded: 0 };

  /** Rótulo da aba com o contador do status entre parênteses (ex.: "Pendentes (12)"). */
  tabLabel(tab: { value: CurationStatus; label: string }): string {
    return `${tab.label} (${this.counts[tab.value]})`;
  }

  // Filtros independentes por grid/status e cópia de trabalho do modal.
  private filtersByStatus: Record<CurationStatus, CurationFilterForm> = {
    pending: emptyFilterForm(),
    curated: emptyFilterForm(),
    discarded: emptyFilterForm()
  };
  advancedFilters: CurationFilterForm = emptyFilterForm();

  // Busca rápida (campo do po-page-list) + ação de busca avançada (modal).
  readonly filter: PoPageFilter = {
    action: this.onQuickSearch.bind(this),
    advancedAction: this.openAdvancedFilter.bind(this),
    placeholder: 'Buscar por arquivo ou código (ex: MATA010, 43307)',
    width: 4
  };

  // Chips de filtros ativos.
  disclaimerGroup: PoDisclaimerGroup = {
    title: 'Filtros ativos',
    disclaimers: [],
    remove: this.onDisclaimerRemove.bind(this),
    removeAll: this.onRemoveAll.bind(this)
  };

  readonly advancedFilterApplyAction: PoModalAction = {
    label: 'Aplicar',
    action: () => this.applyAdvancedFilter()
  };

  readonly advancedFilterCancelAction: PoModalAction = {
    label: 'Cancelar',
    action: () => this.advancedFilterModal.close()
  };

  items: TableRow[] = [];
  total = 0;
  loading = false;
  exporting = false;

  // Período (global à tela; aplicado a todas as abas e contadores).
  period: Period = defaultPeriod();

  // Paginação (show-more) + ordenação server-side.
  // limit default 25, máx 100 no backend; offset default 0. 25 mantém a página enxuta.
  readonly pageSize = 25;
  loadingShowMore = false;
  private sortBy: CurationQueueSortBy = 'falsePositive';
  private sortOrder: CurationSortOrder = 'desc';

  private initialLoadTimer?: number;
  private readonly curatedRemovalDelayMs = 1200;
  private removalTimers = new Set<number>();

  private readonly baseColumns: PoTableColumn[] = [
    { property: 'resourceType', label: 'Tipo', type: 'cellTemplate', width: '120px', sortable: false },
    { property: 'source', label: 'Fonte', type: 'cellTemplate', width: '280px', sortable: false },
    { property: 'displayName', label: 'Review', type: 'cellTemplate', width: '200px', sortable: false },
    { property: 'status', label: 'Status', type: 'cellTemplate', width: '120px', sortable: false },
    { property: 'findingKind', label: 'Achado', type: 'cellTemplate', width: '130px', sortable: false },
    { property: 'info', label: 'Info', type: 'cellTemplate', width: '60px', sortable: false },
    { property: 'feedbackUsers', label: 'Avaliado por', type: 'cellTemplate', width: '140px', sortable: false },
    { property: 'votes', label: 'Votos', type: 'cellTemplate', width: '170px' },
    { property: 'language', label: 'Linguagem', width: '170px', sortable: false },
    // A origem recorta a qualidade entre TFVC, uploads e repositórios git.
    { property: 'sourceProvider', label: 'Origem', width: '150px', sortable: false }
  ];

  private readonly dateColumnPending: PoTableColumn =
    { property: 'pendingSinceLabel', label: 'Na fila desde', width: '170px' };

  private readonly dateColumnCurated: PoTableColumn =
    { property: 'curatedAtLabel', label: 'Curado em', width: '170px' };

  columns: PoTableColumn[] = [...this.baseColumns];

  readonly pageActions: PoPageAction[] = [
    {
      label: 'Exportar golden set (JSON)',
      icon: 'po-icon-download',
      action: () => this.exportGoldenSet(),
      visible: () => this.activeStatus === 'curated'
    },
    { label: 'Atualizar', icon: 'po-icon-refresh', action: () => this.loadQueue() }
  ];

  tableActions: PoTableAction[] = [
    { label: 'Curar', icon: 'po-icon-edit', action: this.openCurate.bind(this) },
    { label: 'Descartar', icon: 'po-icon-delete', type: 'danger', action: this.confirmDiscard.bind(this) }
  ];

  constructor(
    private curationService: CurationService,
    private mainService: MainService,
    private notification: NotificationServiceHM,
    private dialog: PoDialogService,
    private router: Router,
    private route: ActivatedRoute,
    private curationCounts: CurationCountsService
  ) {}

  ngOnInit(): void {
    this.mainService.validSession();
    // Acesso `smartreview.admin` é garantido pelo SmartReviewAdminGuard na rota pai do hub.
    const statusParam = this.route.snapshot.queryParamMap.get('status') as CurationStatus | null;
    const validStatuses: CurationStatus[] = ['pending', 'curated', 'discarded'];
    if (statusParam && validStatuses.includes(statusParam)) {
      this.activeStatus = statusParam;
    }
    this.restorePrefs();
    document.addEventListener('visibilitychange', this.saveColumnsPrefOnHide);
    this.applyTabConfig();
    this.scheduleInitialLoad();
  }

  /**
   * Período e filtros da última visita de volta à tela, antes da primeira carga.
   *
   * A busca rápida fica de fora (mesma regra da tela de review): é consulta pontual, e
   * restaurá-la traria a tela filtrada por um termo que o campo do `po-page-list` nem
   * mostra — filtro invisível é pior que filtro nenhum.
   *
   * Cada lista é conferida contra as opções da tela: um valor de enum que saiu numa
   * versão anterior viraria filtro que o backend não reconhece, com um chip ativo que o
   * usuário não relaciona a nada.
   */
  private restorePrefs(): void {
    const saved = readPref(UI_PREF_KEYS.curationQueueFilters, (raw): CurationQueuePrefs | null => {
      if (!raw || typeof raw !== 'object') { return null; }
      const value = raw as Record<string, unknown>;
      const byStatus = (value['filtersByStatus'] ?? {}) as Record<string, unknown>;

      return {
        period: rehydratePeriod(value['period']),
        filtersByStatus: {
          pending: this.sanitizeFilterForm(byStatus['pending']),
          curated: this.sanitizeFilterForm(byStatus['curated']),
          discarded: this.sanitizeFilterForm(byStatus['discarded'])
        }
      };
    });

    if (!saved) { return; }

    if (saved.period) { this.period = saved.period; }
    this.filtersByStatus = saved.filtersByStatus;
    this.updateDisclaimers();
  }

  private sanitizeFilterForm(raw: unknown): CurationFilterForm {
    if (!raw || typeof raw !== 'object') { return emptyFilterForm(); }

    const value = raw as Record<string, unknown>;
    const values = (options: PoSelectOption[]) => options.map(option => String(option.value));

    return {
      kind: sanitizeStringArray(value['kind'], values(this.kindOptions)),
      findingKind: sanitizeStringArray(value['findingKind'], values(this.findingKindOptions)),
      resolution: sanitizeStringArray(value['resolution'], values(this.resolutionOptions)),
      resourceType: sanitizeStringArray(value['resourceType'], values(this.resourceTypeOptions)),
      sourceProvider: sanitizeStringArray(value['sourceProvider'], values(this.sourceProviderOptions)),
      resourceId: sanitizeString(value['resourceId']),
      search: '',
      language: sanitizeStringArray(value['language'], values(this.languageOptions)),
      category: sanitizeStringArray(value['category'], values(this.categoryOptions)),
      lever: sanitizeStringArray(value['lever'], values(this.leverOptions)),
      curatedBy: sanitizeString(value['curatedBy']),
      feedbackUser: sanitizeString(value['feedbackUser'])
    };
  }

  private savePrefs(): void {
    const withoutSearch = (form: CurationFilterForm) => ({ ...form, search: '' });

    writePref(UI_PREF_KEYS.curationQueueFilters, {
      period: this.period,
      filtersByStatus: {
        pending: withoutSearch(this.filtersByStatus.pending),
        curated: withoutSearch(this.filtersByStatus.curated),
        discarded: withoutSearch(this.filtersByStatus.discarded)
      }
    });
  }

  /**
   * Fotografa as colunas ao sair da tela — por navegação (`ngOnDestroy`) ou ao deixar a
   * página (`visibilitychange`).
   *
   * Necessário porque nem toda mudança do gerenciador emite `p-change-visible-columns`:
   * desafixar uma coluna não altera a lista de visíveis nem a ordem dela, então o evento
   * não dispara e o pino removido continuaria salvo, voltando no carregamento seguinte.
   * Aqui o estado vem do array vivo do po-table, que reflete tudo — inclusive o que o
   * evento cala. Ver `toColumnsPref`.
   *
   * O `visibilitychange` é o que cobre F5 e fechar a aba, justamente os casos em que o
   * pino teimoso apareceria: o Angular não roda `ngOnDestroy` quando a página é
   * descarregada, só quando o roteador troca de tela. É ele, e não `beforeunload`, porque
   * `beforeunload` não dispara de forma confiável no descarte de aba em iOS/Android.
   */
  /** Referência estável para dar e tirar do listener — arrow, para não perder o `this`. */
  private readonly saveColumnsPrefOnHide = (): void => {
    if (document.visibilityState === 'hidden') { this.saveColumnsPref(); }
  };

  private saveColumnsPref(): void {
    const key = this.columnsPrefKey;
    // Só atualiza o que já existe. Sem esta guarda, sair de uma tela nunca personalizada
    // gravaria o padrão de HOJE como preferência, e uma futura reordenação das colunas na
    // definição da tela nunca chegaria a quem nunca pediu nada.
    if (!this.poTable || !hasPref(key)) { return; }
    writePref(key, toColumnsPref(this.poTable.columns));
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.saveColumnsPrefOnHide);
    this.saveColumnsPref();
    if (this.initialLoadTimer) {
      window.clearTimeout(this.initialLoadTimer);
    }
    this.removalTimers.forEach(timer => window.clearTimeout(timer));
    this.removalTimers.clear();
  }

  get filters(): CurationFilterForm {
    return this.filtersByStatus[this.activeStatus];
  }

  set filters(value: CurationFilterForm) {
    this.filtersByStatus[this.activeStatus] = cloneFilterForm(value);
  }

  get isPending(): boolean {
    return this.activeStatus === 'pending';
  }

  get isCurationTab(): boolean {
    return this.activeStatus === 'curated' || this.activeStatus === 'discarded';
  }

  get allLoaded(): boolean {
    return this.items.length >= this.total;
  }

  /** Período alterado: recarrega a aba ativa (os contadores vêm na resposta). */
  onPeriodChange(period: Period): void {
    this.period = period;
    this.loadQueue();
  }

  /**
   * Colunas mudaram no gerenciador. A chave leva o status da aba: cada aba tem um
   * conjunto de colunas diferente (Pendentes não tem "Curado por" nem "Categoria"), e
   * uma preferência só serviria para reordenar colunas que a outra aba nem mostra.
   *
   * Ver `onColumnsChange` da tela de review para por que o alfinete pode chegar só na
   * alteração seguinte.
   */
  onColumnsChange(visibleProperties: string[]): void {
    writePref(
      this.columnsPrefKey,
      toColumnsPref(this.poTable?.columns ?? this.columns, visibleProperties)
    );
  }

  /**
   * "Restaurar padrão" no gerenciador — só desta aba, pelo mesmo motivo da chave.
   *
   * Remonta as colunas depois de apagar: o po-table considera "padrão" o primeiro array
   * que recebeu, que aqui já era a preferência aplicada. Sem remontar, restaurar apagaria
   * o que está salvo e devolveria a tabela à preferência apagada.
   *
   * `applyTabColumns`, e não `applyTabConfig`: este é sobre colunas, e o outro também
   * reaplicaria a ordenação padrão — ver o porquê lá.
   */
  onColumnsRestore(): void {
    clearPref(this.columnsPrefKey);
    this.applyTabColumns();
  }

  private get columnsPrefKey(): string {
    return `${UI_PREF_KEYS.curationQueueColumns}:${this.activeStatus}`;
  }

  /** As colunas da aba com a preferência do usuário aplicada por cima. */
  private withColumnsPref(columns: PoTableColumn[]): PoTableColumn[] {
    return applyColumnsPref(columns, readPref(this.columnsPrefKey, sanitizeColumnsPref));
  }

  selectTab(status: CurationStatus): void {
    if (this.activeStatus === status) { return; }
    this.activeStatus = status;
    this.router.navigate([], {
      queryParams: { status: status === 'pending' ? null : status },
      replaceUrl: true
    });
    this.applyTabConfig();
    this.updateDisclaimers();
    this.loadQueue();
  }

  /** Busca rápida unificada: casa nome de arquivo OU código do review (param `q`). */
  onQuickSearch(term: string): void {
    this.filters.search = (term || '').trim();
    this.loadQueue();
    this.updateDisclaimers();
  }

  /** Abre o modal de busca avançada com uma cópia dos filtros atuais. */
  openAdvancedFilter(): void {
    this.advancedFilters = cloneFilterForm(this.filters);
    this.advancedFilterModal.open();
  }

  /** Aplica os filtros do modal (preserva a busca rápida). */
  applyAdvancedFilter(): void {
    this.filters = { ...cloneFilterForm(this.advancedFilters), search: this.filters.search };
    this.advancedFilterModal.close();
    this.loadQueue();
    this.updateDisclaimers();
  }

  /** Remove um filtro pelo chip. */
  onDisclaimerRemove({ removedDisclaimer }: { removedDisclaimer: PoDisclaimer; currentDisclaimers: PoDisclaimer[] }): void {
    const property = removedDisclaimer.property as keyof CurationFilterForm | undefined;
    if (!property) { return; }
    if (property === 'search') {
      this.poPageList.clearInputSearch();
    }
    this.clearFilter(property);
    this.loadQueue();
    this.updateDisclaimers();
  }

  /** Limpa todos os filtros. */
  onRemoveAll(): void {
    this.filters = emptyFilterForm();
    this.poPageList.clearInputSearch();
    this.loadQueue();
    this.disclaimerGroup = { ...this.disclaimerGroup, disclaimers: [] };
  }

  /** (Re)constrói os chips de filtros ativos. */
  private updateDisclaimers(): void {
    const f = this.filters;
    const disclaimers: PoDisclaimer[] = [];
    const pushText = (property: keyof CurationFilterForm, label: string) => {
      const value = f[property];
      if (typeof value === 'string' && value) { disclaimers.push({ property, value, label }); }
    };
    const pushMulti = (property: MultiFilterKey, title: string, options: PoSelectOption[]) => {
      const value = f[property];
      if (value.length) {
        disclaimers.push({ property, value, label: `${title}: ${this.optionLabels(options, value)}` });
      }
    };

    pushText('search', `Busca: ${f.search}`);
    pushMulti('kind', 'Tipo de achado', this.kindOptions);
    pushMulti('findingKind', 'Tipo de item', this.findingKindOptions);
    if (this.isPending) {
      pushMulti('resolution', 'Resolução', this.resolutionOptions);
    }
    pushMulti('resourceType', 'Tipo de review', this.resourceTypeOptions);
    pushMulti('sourceProvider', 'Origem', this.sourceProviderOptions);
    pushText('resourceId', `Código: ${f.resourceId}`);
    pushMulti('language', 'Linguagem', this.languageOptions);
    if (this.isCurationTab) {
      pushMulti('category', 'Categoria', this.categoryOptions);
      pushMulti('lever', 'Alavanca de melhoria (lever)', this.leverOptions);
      pushText('curatedBy', `Curado por: ${f.curatedBy}`);
    }
    pushText('feedbackUser', `Avaliado por: ${f.feedbackUser}`);

    this.disclaimerGroup = { ...this.disclaimerGroup, disclaimers };
  }

  private optionLabel(options: PoSelectOption[], value: string): string {
    return (options.find(option => option.value === value)?.label as string) || value;
  }

  private optionLabels(options: PoSelectOption[], values: string[]): string {
    return values.map(value => this.optionLabel(options, value)).join(', ');
  }

  private clearFilter(property: keyof CurationFilterForm): void {
    if (isMultiFilterKey(property)) {
      this.filters[property] = [];
      return;
    }
    this.filters[property] = '';
  }

  private isStatusVisible(status: CurationStatus): boolean {
    return this.activeStatus === status;
  }

  private validResourceTypes(resourceTypes: string[]): string[] {
    const validValues = new Set(this.resourceTypeOptions.map(option => String(option.value)));
    return resourceTypes.filter(value => validValues.has(value));
  }

  /**
   * O recorte é salvo aqui, e não em cada handler: período, filtro avançado, remoção de
   * chip e troca de aba terminam todos em `loadQueue()`, então é o único ponto que não
   * dá para esquecer ao acrescentar um filtro novo. Mesmo desenho do painel de custos.
   */
  loadQueue(): void {
    this.savePrefs();
    this.loading = true;

    this.curationService.getQueue(this.buildQueueFilters(0)).subscribe({
      next: res => {
        this.total = res?.total ?? 0;
        this.counts[this.activeStatus] = this.total;
        this.items = this.buildRows(res?.items ?? []);
        const hasServerCounts = this.applyResponseCounts(res?.counts);
        // Só propaga pendentes ao badge quando o número é confiável: aba pendente
        // (counts.pending = total recém-carregado) ou counts cross-status do backend.
        if (this.activeStatus === 'pending' || hasServerCounts) {
          this.curationCounts.setPending(this.counts.pending);
        }
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.items = [];
        this.total = 0;
        this.notification.showToast(getApiErrorMessage(error, 'Erro ao carregar a fila de curadoria.'), 'error');
      }
    });
  }

  /** Carrega a próxima página e anexa ao grid (mantém a ordenação do backend). */
  onShowMore(): void {
    if (this.allLoaded || this.loadingShowMore) { return; }
    this.loadingShowMore = true;
    // Offset pelo nº de linhas já carregadas (não por página fixa): curar/descartar
    // remove itens do grid e do total do servidor, então paginar por página
    // (currentPage * pageSize) deslocaria o offset e pularia itens. items.length
    // sempre aponta para o próximo item ainda não carregado do servidor.
    const offset = this.items.length;

    this.curationService.getQueue(this.buildQueueFilters(offset)).subscribe({
      next: res => {
        this.total = res?.total ?? this.total;
        this.items = [...this.items, ...this.buildRows(res?.items ?? [])];
        this.loadingShowMore = false;
      },
      error: error => {
        this.loadingShowMore = false;
        this.notification.showToast(getApiErrorMessage(error, 'Erro ao carregar mais itens.'), 'error');
      }
    });
  }

  /** Ordenação acionada pela po-table — só "Votos" (falsePositive) e a coluna de data (updatedAt). */
  onSortBy(event: { column: PoTableColumn; type: 'ascending' | 'descending' }): void {
    const map: Record<string, CurationQueueSortBy> = {
      votes: 'falsePositive',
      pendingSinceLabel: 'updatedAt',
      curatedAtLabel: 'updatedAt'
    };
    const sortBy = map[event?.column?.property ?? ''];
    if (!sortBy) { return; }
    this.sortBy = sortBy;
    this.sortOrder = event.type === 'ascending' ? 'asc' : 'desc';
    this.loadQueue();
  }

  /** Monta os filtros do GET /queue a partir do estado atual + período + paginação. */
  private buildQueueFilters(offset: number): CurationQueueFilters {
    const f = this.filters;
    const filters: CurationQueueFilters = {
      status: this.activeStatus,
      limit: this.pageSize.toString(),
      offset: offset.toString(),
      sortBy: this.sortBy,
      sortOrder: this.sortOrder
    };
    if (this.period.dateFrom) { filters.dateFrom = this.period.dateFrom; }
    if (this.period.dateTo) { filters.dateTo = this.period.dateTo; }
    if (f.kind.length) { filters.kind = f.kind as CurationKind[]; }
    if (f.findingKind.length) { filters.findingKind = f.findingKind; }
    const resourceTypes = this.validResourceTypes(f.resourceType);
    if (resourceTypes.length) { filters.resourceType = resourceTypes; }
    if (f.sourceProvider.length) { filters.sourceProvider = f.sourceProvider; }
    if (f.resourceId.trim()) { filters.resourceId = f.resourceId.trim(); }
    if (f.search.trim()) { filters.search = f.search.trim(); }
    if (f.language.length) { filters.language = f.language; }
    if (f.feedbackUser.trim()) { filters.feedbackUser = f.feedbackUser.trim(); }
    if (this.isPending && f.resolution.length) { filters.resolution = f.resolution; }
    if (this.isCurationTab) {
      if (f.category.length) { filters.category = f.category; }
      if (f.lever.length) { filters.lever = f.lever; }
      if (f.curatedBy.trim()) { filters.curatedBy = f.curatedBy.trim(); }
    }
    return filters;
  }

  /**
   * Ajusta os contadores das abas (Pendentes/Curados/Descartados) após curar ou
   * descartar, sem refazer a query, e propaga o total de pendentes para o badge
   * da aba "Curadoria" no shell.
   */
  private adjustCounts(from: CurationStatus, to: CurationStatus): void {
    if (from === to) { return; }
    this.counts = {
      ...this.counts,
      [from]: Math.max(0, (this.counts[from] ?? 0) - 1),
      [to]: (this.counts[to] ?? 0) + 1
    };
    this.curationCounts.setPending(this.counts.pending);
  }

  /**
   * Aplica os contadores devolvidos pelo /queue (quando o backend os envia).
   * Retorna `true` se aplicou (counts cross-status confiáveis), `false` caso contrário.
   */
  private applyResponseCounts(counts?: CurationQueueResponse['counts']): boolean {
    if (!counts) { return false; }
    this.counts = {
      pending: counts.pending ?? this.counts.pending,
      curated: counts.curated ?? this.counts.curated,
      discarded: counts.discarded ?? this.counts.discarded
    };
    return true;
  }

  /** Exporta o golden set (JSON) com os filtros da aba Curados + período. */
  exportGoldenSet(): void {
    if (this.exporting) { return; }
    this.exporting = true;
    this.curationService.exportGoldenSet(this.buildExportFilters()).subscribe({
      next: response => {
        this.exporting = false;
        this.downloadJson(response.body ?? '', this.resolveFilename(response.headers.get('Content-Disposition')));
        this.notification.showToast('Golden set exportado.', 'success');
      },
      error: error => {
        this.exporting = false;
        this.notification.showToast(getApiErrorMessage(error, 'Erro ao exportar o golden set.'), 'error');
      }
    });
  }

  /** Filtros do GET /export a partir do estado da aba Curados + período. */
  private buildExportFilters(): CurationExportFilters {
    const f = this.filters;
    const filters: CurationExportFilters = {};
    if (this.period.dateFrom) { filters.dateFrom = this.period.dateFrom; }
    if (this.period.dateTo) { filters.dateTo = this.period.dateTo; }
    if (f.kind.length) { filters.kind = f.kind as CurationKind[]; }
    if (f.category.length) { filters.category = f.category as CurationExportFilters['category']; }
    if (f.lever.length) { filters.lever = f.lever; }
    const resourceTypes = this.validResourceTypes(f.resourceType);
    if (resourceTypes.length) { filters.resourceType = resourceTypes; }
    if (f.sourceProvider.length) { filters.sourceProvider = f.sourceProvider; }
    if (f.resourceId.trim()) { filters.resourceId = f.resourceId.trim(); }
    if (f.search.trim()) { filters.search = f.search.trim(); }
    if (f.language.length) { filters.language = f.language; }
    if (f.curatedBy.trim()) { filters.curatedBy = f.curatedBy.trim(); }
    if (f.feedbackUser.trim()) { filters.feedbackUser = f.feedbackUser.trim(); }
    return filters;
  }

  private resolveFilename(contentDisposition: string | null): string {
    const match = contentDisposition?.match(/filename="?([^"]+)"?/i);
    return match?.[1]?.trim() || 'golden_set.json';
  }

  private downloadJson(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private scheduleInitialLoad(): void {
    this.initialLoadTimer = window.setTimeout(() => {
      this.initialLoadTimer = undefined;
      this.loadQueue();
    });
  }

  private applyTabConfig(): void {
    this.applyDefaultSort();
    this.applyTabColumns();
    this.applyTabActions();
  }

  /**
   * Só as colunas da aba ativa, com a preferência do usuário por cima.
   *
   * Separado de `applyTabConfig` porque "Restaurar padrão" no gerenciador precisa
   * remontar as colunas SEM tocar em mais nada: `applyTabConfig` também reaplica a
   * ordenação padrão, e fazer isso sem recarregar deixaria `sortBy`/`sortOrder`
   * divergindo do que está na tela — o "carregar mais" seguinte pediria um offset
   * sobre um resultado ordenado de outro jeito, repetindo linhas já exibidas e
   * pulando outras.
   */
  private applyTabColumns(): void {
    const dateColumn = this.isPending ? this.dateColumnPending : this.dateColumnCurated;

    if (this.isPending) {
      this.columns = this.withColumnsPref([...this.baseColumns, dateColumn]);
    } else {
      // Nas abas Curados/Descartados há mais colunas, então reduzimos Fonte e Review
      // para dar espaço adequado às colunas de curadoria.
      const curationBaseColumns = this.baseColumns.map(col => {
        if (col.property === 'source') return { ...col, width: '220px' };
        if (col.property === 'displayName') return { ...col, width: '200px' };
        return col;
      });
      this.columns = this.withColumnsPref([
        ...curationBaseColumns,
        dateColumn,
        { property: 'curatedBy', label: 'Curado por', width: '170px', sortable: false },
        ...( this.activeStatus === 'curated'
          ? [{ property: 'categoryLabel', label: 'Categoria', width: '200px', sortable: false }]
          : []
        )
      ]);
    }
  }

  private applyTabActions(): void {
    this.tableActions = this.isPending
      ? [
          { label: 'Curar', icon: 'po-icon-edit', action: this.openCurate.bind(this) },
          { label: 'Descartar', icon: 'po-icon-delete', type: 'danger', action: this.confirmDiscard.bind(this) }
        ]
      : this.activeStatus === 'discarded'
      ? [
          { label: 'Visualizar', icon: 'po-icon-eye', action: this.openView.bind(this) },
          { label: 'Reclassificar', icon: 'po-icon-edit', action: this.openCurate.bind(this) }
        ]
      : [
          { label: 'Reclassificar', icon: 'po-icon-edit', action: this.openCurate.bind(this) }
        ];
  }

  /**
   * Ordenação default por aba (resetada ao trocar de aba):
   * - Pendentes: `falsePositive desc` — fila de trabalho prioriza os achados de
   *   maior sinal (mais votos de falso positivo), os mais valiosos para o golden set.
   * - Curados/Descartados: `updatedAt desc` — visão de histórico mostra o que foi
   *   curado/descartado mais recentemente primeiro.
   */
  private applyDefaultSort(): void {
    this.sortBy = this.isPending ? 'falsePositive' : 'updatedAt';
    this.sortOrder = 'desc';
  }

  /** Item descartado via modal: mesma lógica do discard direto do grid. */
  onDiscardedFromModal(item: CurationQueueItem): void {
    const oldStatus = (item.status as CurationStatus) || 'pending';
    this.adjustCounts(oldStatus, 'discarded');
    this.markRowStatus(item, 'discarded');
    if (!this.isStatusVisible('discarded')) {
      this.removeItem(item);
    }
  }

  openCurate(row: TableRow): void {
    this.curationModal.open(row.item, 'curate');
  }

  openView(row: TableRow): void {
    this.curationModal.open(row.item, 'view');
  }

  /**
   * Item curado/reclassificado: atualiza a linha se o novo status ainda está
   * visível pelo filtro; caso contrário, pisca o badge e remove.
   */
  onCurated(event: CurationCuratedEvent): void {
    const { item, result } = event;
    const oldStatus = (item.status as CurationStatus) || 'pending';
    const newStatus = (result?.status as CurationStatus) || 'curated';
    this.adjustCounts(oldStatus, newStatus);

    if (this.isStatusVisible(newStatus)) {
      this.updateRow(item, result);
      return;
    }

    this.markRowStatus(item, newStatus);

    const timer = window.setTimeout(() => {
      this.removalTimers.delete(timer);
      this.removeItem(item);
    }, this.curatedRemovalDelayMs);
    this.removalTimers.add(timer);
  }

  confirmDiscard(row: TableRow): void {
    this.dialog.confirm({
      title: 'Descartar feedback',
      message: `Descartar o feedback de <strong>${row.resource}</strong>?`,
      confirm: () => this.discard(row.item),
      cancel: () => {}
    });
  }

  private discard(item: CurationQueueItem): void {
    const oldStatus = (item.status as CurationStatus) || 'pending';
    this.curationService
      .discardItem({
        resourceType: item.resourceType,
        resourceId: item.resourceId,
        findingKind: item.findingKind,
        findingId: item.findingId
      })
      .subscribe({
        next: () => {
          this.notification.showToast('Item descartado.', 'success');
          this.adjustCounts(oldStatus, 'discarded');
          this.markRowStatus(item, 'discarded');
          if (!this.isStatusVisible('discarded')) {
            this.removeItem(item);
          }
        },
        error: error => {
          this.notification.showToast(getApiErrorMessage(error, 'Erro ao descartar o feedback.'), 'error');
        }
      });
  }

  /** Atualiza o status (badge) da linha do item, preservando a identidade da linha. */
  private markRowStatus(item: CurationQueueItem, status: string): void {
    item.status = status;
    this.items = this.items.map(row => (row.item === item ? { ...row, status } : row));
  }

  /** Atualiza a linha curada/reclassificada in-place (status/categoria/curador). */
  private updateRow(item: CurationQueueItem, result: CurateResponse): void {
    this.items = this.items.map(row =>
      row.item === item
        ? {
            ...row,
            status: result?.status || row.status,
            curatedBy: result?.curatedBy || row.curatedBy,
            categoryLabel: categoryLabel(result?.category) || row.categoryLabel,
            // formatCurationDateTime devolve 'n/d' (truthy) sem valor, então o fallback
            // precisa ser decidido antes de formatar.
            curatedAtLabel: result?.curatedAt ? formatCurationDateTime(result.curatedAt) : row.curatedAtLabel,
            item: { ...row.item, ...result }
          }
        : row
    );
  }

  private removeItem(item: CurationQueueItem): void {
    this.items = this.items.filter(row => row.item !== item);
    this.total = Math.max(0, this.total - 1);
  }

  /** Monta linhas planas da tabela, preservando a ordem (paginada) do backend. */
  private buildRows(items: CurationQueueItem[]): TableRow[] {
    return items.map(item => {
        const snapshot = buildCurationSnapshotView(item);
        const feedbackUsers = getCurationFeedbackUsers(item);

        const row: TableRow = {
          kind: item.kind === 'review' ? 'Review' : 'Achado',
          resourceType: item.resourceType,
          resource: formatCurationReviewLabel(item),
          displayName: formatCurationReviewLabel(item),
          source: item.fileName || '',
          findingKind: item.findingKind,
          fileName: item.fileName || '',
          filePath: item.filePath || '',
          description: this.truncate(snapshot.description, 90),
          line: snapshot.line || '',
          severity: formatSeverityLabel(snapshot.severity) || '',
          status: item.status,
          votes: item.votes,
          language: item.language || '',
          sourceProvider: sourceProviderLabel(item.sourceProvider),
          feedbackUsers,
          curatedBy: item.curatedBy || '',
          categoryLabel: categoryLabel(item.category),
          pendingSinceLabel: formatCurationDateTime(item.pendingSince),
          curatedAtLabel: formatCurationDateTime(item.curatedAt),
          // Rótulos estáticos pré-computados aqui (não em getters do template, que
          // recalculariam — inclusive um Array.find por linha — a cada ciclo de CD).
          resourceTypeLabel: '',
          findingKindLabel: '',
          feedbackUsersLabel: '',
          tooltip: '',
          item
        };

        row.resourceTypeLabel = this.getResourceTypeLabel(row);
        row.findingKindLabel = this.getFindingKindLabel(row);
        row.feedbackUsersLabel = this.getFeedbackUsersLabel(row);
        row.tooltip = this.buildTooltip(row);
        return row;
      });
  }

  private truncate(value: string | undefined, max: number): string {
    if (!value) { return ''; }
    return value.length > max ? `${value.slice(0, max)}…` : value;
  }

  getResourceTypeLabel(row: TableRow): string {
    // Do mapa compartilhado, e não da lista do filtro: um tipo que a fila recebe mas que
    // ninguém filtra — as variantes -upload, por exemplo — aparecia com o valor cru.
    return resourceTypeLabel(row.resourceType) || 'n/d';
  }

  getFindingKindLabel(row: TableRow): string {
    if (row.kind === 'Review' || row.findingKind === 'overall') {
      return 'Geral';
    }
    const labels: Record<string, string> = {
      required_action: 'Ação',
      suggestion: 'Sugestão'
    };
    return labels[(row.findingKind || '').toLowerCase()] || row.findingKind || 'n/d';
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      pending: 'Pendente',
      curated: 'Curado',
      discarded: 'Descartado'
    };
    return labels[(status || '').toLowerCase()] || status || 'n/d';
  }

  getStatusBadgeType(status: string): string {
    const types: Record<string, string> = {
      pending: 'warning',
      curated: 'success',
      discarded: 'neutral'
    };
    return types[(status || '').toLowerCase()] || 'info';
  }

  getFeedbackUsersLabel(row: TableRow): string {
    const total = row.feedbackUsers.length;
    if (!total) { return ''; }
    return total === 1 ? '1 dev' : `${total} devs`;
  }

  buildTooltip(row: TableRow): string {
    const lines = [
      `Recurso: ${row.resource}`,
      row.fileName ? `Fonte: ${row.fileName}` : '',
      row.filePath ? `Caminho: ${row.filePath}` : '',
      row.sourceProvider ? `Origem: ${row.sourceProvider}` : '',
      row.line ? `Linha: ${row.line}` : '',
      row.severity ? `Severidade: ${row.severity}` : '',
      row.language ? `Linguagem: ${row.language}` : '',
      `Votos: TP ${row.votes?.truePositive ?? 0} / FP ${row.votes?.falsePositive ?? 0} / WF ${row.votes?.wontFix ?? 0}`,
      row.feedbackUsers.length ? `Usuários: ${row.feedbackUsers.join(', ')}` : '',
      row.description ? `Descrição: ${row.description}` : ''
    ];
    return lines.filter(Boolean).join('\n');
  }
}

/** Estado do formulário de filtros (busca rápida + busca avançada). */
/**
 * O que a fila guarda no navegador entre visitas. `period` vem nulo quando o que estava
 * salvo não descreve um recorte utilizável — a tela fica com o padrão de 30 dias.
 */
interface CurationQueuePrefs {
  period: Period | null;
  filtersByStatus: Record<CurationStatus, CurationFilterForm>;
}

interface CurationFilterForm {
  kind: string[];
  findingKind: string[];
  resolution: string[];
  resourceType: string[];
  sourceProvider: string[];
  resourceId: string;
  search: string;
  language: string[];
  category: string[];
  lever: string[];
  curatedBy: string;
  feedbackUser: string;
}

type MultiFilterKey = 'kind' | 'findingKind' | 'resolution' | 'resourceType' | 'sourceProvider' | 'language' | 'category' | 'lever';

const MULTI_FILTER_KEYS: MultiFilterKey[] = ['kind', 'findingKind', 'resolution', 'resourceType', 'sourceProvider', 'language', 'category', 'lever'];

function emptyFilterForm(): CurationFilterForm {
  return {
    kind: [], findingKind: [], resolution: [], resourceType: [], sourceProvider: [], resourceId: '',
    search: '', language: [], category: [], lever: [], curatedBy: '', feedbackUser: ''
  };
}

function cloneFilterForm(form: CurationFilterForm): CurationFilterForm {
  return {
    ...form,
    kind: [...form.kind],
    findingKind: [...form.findingKind],
    resolution: [...form.resolution],
    resourceType: [...form.resourceType],
    sourceProvider: [...form.sourceProvider],
    language: [...form.language],
    category: [...form.category],
    lever: [...form.lever]
  };
}

function isMultiFilterKey(property: keyof CurationFilterForm): property is MultiFilterKey {
  return MULTI_FILTER_KEYS.includes(property as MultiFilterKey);
}

/** Linha plana da po-table com referência ao item de domínio original. */
interface TableRow {
  kind: string;
  resourceType: string;
  resource: string;
  displayName: string;
  source: string;
  findingKind: string;
  fileName: string;
  filePath: string;
  description: string;
  line: string;
  severity: string;
  status: string;
  votes: CurationQueueItem['votes'];
  language: string;
  sourceProvider: string;
  feedbackUsers: string[];
  curatedBy: string;
  categoryLabel: string;
  pendingSinceLabel: string;
  curatedAtLabel: string;
  resourceTypeLabel: string;
  findingKindLabel: string;
  feedbackUsersLabel: string;
  tooltip: string;
  item: CurationQueueItem;
}
